import { ROUTE_COLORS } from '@/components/DispatchPlan/constants';
import { useClientUser } from '@/hooks';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import csvDownload from 'json-to-csv-export';
import { createContext, useCallback, useMemo, useState } from 'react';
import { SUBREGION_AUTO_ASSIGN_EXPORT_COLUMNS } from '../constants';
import { QUERY_CARRIERS, QUERY_ASSIGN_OVERRIDES, QUERY_SUBREGIONS_MANY } from '../graphql/queries';
import { useSubregionCallbacks, useSubregionState } from '../hooks';
import { QUERY_CLIENTS } from '../AutoAssign/graphql';
import { exportMapping } from '../AutoAssign/utils';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const [loading, setLoading] = useState({});
    const [selected, selectObject] = useState({});
    const [hovered, hoverObject] = useState({});
    const [filter, setFilter] = useState({});
    const [notification, setNotification] = useState({});
    const [testFlag, setTestFlag] = useState(false);

    const { data: poData } = useQuery(QUERY_ASSIGN_OVERRIDES, {
        variables: {
            test: testFlag,
        },
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const [shippersById, carriersById] = useMemo(() => {
        let shippers = {},
            carriers = {};

        (poData?.results || []).forEach((pricing_override) => {
            shippers[pricing_override.client_id] = pricing_override.client;
            carriers[pricing_override.assign_id] = pricing_override.assign;
        });

        return [shippers, carriers];
    }, [poData]);

    const { data: subregionData, loading: querySubregionsLoading } = useQuery(QUERY_SUBREGIONS_MANY, {
        variables: { client_ids: Object.keys(carriersById) },
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const [subregions, locationsByClient] = useMemo(() => {
        return [
            subregionData?.results || [],
            (subregionData?.locations || []).reduce((acc, location) => {
                return {
                    ...acc,
                    [location.client_id]: [...(acc[location.client_id] || []), location],
                };
            }, {}),
        ];
    }, [subregionData]);

    const colorsByClientId = useMemo(() => {
        return Object.fromEntries(
            Object.entries(carriersById).map(([client_id], idx) => [client_id, ROUTE_COLORS[idx % ROUTE_COLORS.length]])
        );
    }, [ROUTE_COLORS, carriersById]);

    const { isOnwardAdmin } = useClientUser();

    const { data: clientsData } = useQuery(QUERY_CLIENTS, {
        skip: !isOnwardAdmin,
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const { data: rateShopCarriersData } = useQuery(QUERY_CARRIERS, {
        variables: { test: testFlag },
        skip: isOnwardAdmin,
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const exportEnrichments = useMemo(() => {
        if (isOnwardAdmin) {
            return {
                carriersById: Object.fromEntries((clientsData?.carriers || []).map((c) => [c.client_id, c])),
                mmCarriersById: Object.fromEntries((clientsData?.mm_carriers || []).map((c) => [c.client_id, c])),
                warehouseCarriersById: Object.fromEntries(
                    (clientsData?.carriers || []).filter((c) => c.locations?.length).map((c) => [c.client_id, c])
                ),
            };
        }
        const list = rateShopCarriersData?.results || [];
        return {
            carriersById: Object.fromEntries(list.map((c) => [c.client_id, c])),
            mmCarriersById: {},
            warehouseCarriersById: Object.fromEntries(
                list.filter((c) => c.locations?.length).map((c) => [c.client_id, c])
            ),
        };
    }, [isOnwardAdmin, clientsData, rateShopCarriersData]);

    const state = useSubregionState({ subregions, selected, hovered, filter });

    const callbacks = useSubregionCallbacks(
        { query: QUERY_SUBREGIONS_MANY, queryVariables: { client_ids: Object.keys(carriersById) } },
        { setLoading, selectObject, hoverObject, setNotification }
    );

    const exportSubregions = useCallback(() => {
        const emptyMapping = {
            is_direct: false,
            carrier_id: undefined,
            warehouse_id: undefined,
            middle_mile_carrier_id: undefined,
            warehouse_carrier_id: undefined,
            site_code: undefined,
            site_contact_name: undefined,
            site_contact_email: undefined,
            site_contact_phone: undefined,
        };

        const selectedZipLookup = new Set(state.selectedZips || []);
        const subregionsToExport = state.selectedSubregion ? [state.selectedSubregion] : subregions;

        const data = subregionsToExport.reduce((acc, subregion, idx) => {
            const name = callbacks.genName(subregion, idx);
            const zips = (subregion.zips || []).filter(
                ({ zip }) => selectedZipLookup.size === 0 || selectedZipLookup.has(zip)
            );
            if (zips.length === 0) {
                return acc;
            }

            const ownerCarrierId = subregion.client_id;
            const mapping = exportMapping(subregion.carriers);

            return [
                ...acc,
                ...zips.map(({ zip }) => ({
                    name,
                    zip,
                    ...(mapping || emptyMapping),
                    is_direct: mapping?.is_direct ?? false,
                    carrier_id: mapping?.carrier_id || ownerCarrierId,
                    warehouse_id: mapping?.warehouse_id || subregion.warehouse_id,
                    middle_mile_carrier_id: mapping?.middle_mile_carrier_id,
                    warehouse_carrier_id: mapping?.warehouse_carrier_id || mapping?.carrier_id || ownerCarrierId,
                    site_code: mapping?.site_code,
                    site_contact_name: mapping?.site_contact_name,
                    site_contact_email: mapping?.site_contact_email,
                    site_contact_phone: mapping?.site_contact_phone,
                })),
            ];
        }, []);

        const columns = SUBREGION_AUTO_ASSIGN_EXPORT_COLUMNS;

        csvDownload({
            headers: columns.map((col) => col.header),
            data: data.map((row) => columns.map((col) => col.value(row, exportEnrichments))),
            filename: `carrier-regions-${format(new Date(), 'yyyy-MM-dd')}.csv`,
            delimiter: ',',
        });
    }, [subregions, callbacks, exportEnrichments, state.selectedSubregion, state.selectedZips]);

    return (
        <Context.Provider
            value={{
                state: {
                    ...state,
                    notification,
                    shippersById,
                    carriersById,
                    subregions,
                    locationsByClient,
                    testFlag,
                },
                loading: {
                    subregions: loading.subregions || querySubregionsLoading,
                    zips: loading.zips || querySubregionsLoading,
                    import: loading.import,
                },
                callbacks: {
                    ...callbacks,
                    setNotification,
                    selectObject,
                    hoverObject,
                    setFilter,
                    setTestFlag,
                    genColor: (subregion) => colorsByClientId[subregion.client_id],
                    genOwner: (subregion) => carriersById[subregion.client_id]?.business_name || 'Unknown Carrier',
                    exportSubregions,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
